import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type AtlasInfo, IMPORT_STATE } from '../types';

export const useAtlas = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(SOURCE_TYPE.ATLAS, workspace_alias);

  const { data } = useQuery({
    queryKey: queryKeys,
    queryFn: () => service.atlas.getAtlasInfo(workspace_alias),
    enabled: enabled,
  });

  const { mutate: disconnectAtlasMutation } = useMutation({
    mutationFn: () => service.atlas.deleteAtlasInfo(workspace_alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.atlas.importAtlasFeedback(workspace_alias, dateRange),
    onSuccess: () => {
      const atlasWorkspace = queryClient.getQueryData(queryKeys) as AtlasInfo;
      queryClient.setQueryData(queryKeys, {
        ...atlasWorkspace,
        import_state: IMPORT_STATE.IMPORTING,
      });
    },
  });

  return {
    atlasWorkspace: data,
    disconnectAtlasMutation,
    requestImportMutation,
  };
};
